import { React, useState } from 'react';

import { FormHelperText } from '@mui/material';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import PriceCheckRoundedIcon from '@mui/icons-material/PriceCheckRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import RequestQuoteRoundedIcon from '@mui/icons-material/RequestQuoteRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';

import { Alerta } from '../../components/alerta';
import DayDatePicker from '../../components/dayDatePicker';
import PageHeader from '../../components/pageHeader';
import SubmitButton from '../../components/submitButton';
import { formatDateLabelPtBr, formatIsoDate } from '../../utils/dateUtils';
import { fetchFlask } from '../../utils/fetchFlask';
import { formatCurrency } from '../../utils/stringFormat';
import '../css/clientBilling.css';

const hasNumericValue = (value) => value !== '' && value !== null && !Number.isNaN(Number(value));

const FaturarCliente = () => {
    const [notaFiscal, setNotaFiscal] = useState('');
    const [valorCobrar, setValorCobrar] = useState('');
    const [dateTimeVencimento, setDateTimeVencimento] = useState(null);
    const [errorVencimento, setErrorVencimento] = useState(false);
    const [message, setMessage] = useState('');
    const [status, setStatus] = useState('');
    const [openAlerta, setOpenAlerta] = useState(false);
    const [isSubmit, setIsSubmit] = useState(false);

    async function submitFaturarCliente (e) {
        e.preventDefault();
        setIsSubmit(true);

        let vencimento = '';
        if (dateTimeVencimento) {
            setErrorVencimento(false);
            vencimento = formatIsoDate(dateTimeVencimento);
        } else {
            setErrorVencimento(true);
            setIsSubmit(false);
            return;
        }

        const body = JSON.stringify({
            notaFiscal,
            valorCobrar,
            vencimento
        });

        const response = await fetchFlask('faturarCliente', body);

        setMessage(response.message);
        setStatus(response.status);
        setOpenAlerta(true);
        if (response.status === 'success') {
            setNotaFiscal('');
            setValorCobrar('');
            setDateTimeVencimento(null);
        }
        setIsSubmit(false);
    };

    const vencimentoLabel = formatDateLabelPtBr(dateTimeVencimento);
    const valorLabel = hasNumericValue(valorCobrar) ? formatCurrency(Number(valorCobrar)) : 'Não informado';
    const completedFields = [
        notaFiscal,
        hasNumericValue(valorCobrar),
        dateTimeVencimento
    ].filter(Boolean).length;

    return (
        <Box className='mainBox client-billing-page'>
            <Alerta
                texto={message}
                status={status}
                open={openAlerta}
                handleClose={() => setOpenAlerta(false)}
            />
            <PageHeader>
                Faturar Cliente
            </PageHeader>

            <form action='' method='POST' id='formFaturarCliente' onSubmit={submitFaturarCliente} className='client-billing-layout'>
                <Box className='client-billing-workspace'>
                    <Box className='client-billing-intro'>
                        <Box>
                            <h2>Faturamento manual do cliente</h2>
                        </Box>
                        <p>
                            Informe a nota fiscal, o valor e a data de vencimento para gerar a cobrança do cliente.
                        </p>
                    </Box>

                    <Box className='client-billing-panel'>
                        <Box className='client-billing-section-heading'>
                            <ReceiptLongRoundedIcon />
                            <Box>
                                <span>Etapa 1</span>
                                <strong>Nota fiscal</strong>
                            </Box>
                        </Box>

                        <Box className='client-billing-input-strip'>
                            <RequestQuoteRoundedIcon />
                            <TextField
                                required
                                label='Nota fiscal'
                                type='number'
                                inputProps={{ step: 1, min: 0 }}
                                value={notaFiscal}
                                onChange={(e) => setNotaFiscal(e.target.value)}
                            />
                        </Box>
                    </Box>

                    <Box className='client-billing-panel'>
                        <Box className='client-billing-section-heading'>
                            <PaymentsRoundedIcon />
                            <Box>
                                <span>Etapa 2</span>
                                <strong>Valor a cobrar</strong>
                            </Box>
                        </Box>

                        <Box className='client-billing-input-strip'>
                            <PriceCheckRoundedIcon />
                            <TextField
                                required
                                label='Valor'
                                type='number'
                                inputProps={{ step: 0.01, min: 0 }}
                                value={valorCobrar}
                                onChange={(e) => setValorCobrar(e.target.value)}
                            />
                        </Box>
                    </Box>

                    <Box className='client-billing-panel'>
                        <Box className='client-billing-section-heading'>
                            <CalendarMonthRoundedIcon />
                            <Box>
                                <span>Etapa 3</span>
                                <strong>Vencimento da cobrança</strong>
                            </Box>
                        </Box>

                        <Box className='client-billing-input-strip'>
                            <CalendarMonthRoundedIcon />
                            <FormControl className='client-billing-date-control' error={errorVencimento}>
                                <DayDatePicker
                                    id='data'
                                    label='Vencimento'
                                    minDate={new Date('2023-01-02')}
                                    value={dateTimeVencimento}
                                    onChange={setDateTimeVencimento}
                                />
                                {errorVencimento && (
                                    <FormHelperText>Por favor selecione uma data.</FormHelperText>
                                )}
                            </FormControl>
                        </Box>
                    </Box>

                    <Box className='client-billing-rail'>
                        <Box>
                            <ReceiptLongRoundedIcon />
                            <span>Documento</span>
                            <strong>{notaFiscal || 'NF pendente'}</strong>
                        </Box>
                        <Box>
                            <PaymentsRoundedIcon />
                            <span>Valor</span>
                            <strong>{valorLabel}</strong>
                        </Box>
                        <Box>
                            <CalendarMonthRoundedIcon />
                            <span>Vencimento</span>
                            <strong>{vencimentoLabel}</strong>
                        </Box>
                    </Box>
                </Box>

                <Box className='client-billing-summary'>
                    <Box className='client-billing-summary-header'>
                        <FactCheckRoundedIcon />
                        <Box>
                            <span>Resumo</span>
                            <strong>Revisão do faturamento</strong>
                        </Box>
                    </Box>

                    <Box className='client-billing-summary-list'>
                        <Box>
                            <span>Nota fiscal</span>
                            <strong className={!notaFiscal ? 'is-empty' : undefined}>{notaFiscal || 'Não informada'}</strong>
                        </Box>
                        <Box>
                            <span>Valor a cobrar</span>
                            <strong className={!hasNumericValue(valorCobrar) ? 'is-empty' : undefined}>{valorLabel}</strong>
                        </Box>
                        <Box>
                            <span>Vencimento</span>
                            <strong className={!dateTimeVencimento ? 'is-empty' : undefined}>{vencimentoLabel}</strong>
                        </Box>
                    </Box>

                    <Box className='client-billing-attention'>
                        <ReceiptLongRoundedIcon />
                        <p>
                            O faturamento será enviado com a nota fiscal e o vencimento selecionados. Revise o valor antes de confirmar.
                        </p>
                    </Box>

                    <Box className='client-billing-completion'>
                        <AssignmentTurnedInRoundedIcon />
                        <Box>
                            <span>Campos críticos</span>
                            <strong>{completedFields}/3 preenchidos</strong>
                        </Box>
                    </Box>

                    <SubmitButton isSubmit={isSubmit} startIcon={<SendRoundedIcon />}>
                        Faturar Cliente
                    </SubmitButton>
                </Box>
            </form>
        </Box>
    );
};

export default FaturarCliente;
